import React, { useState, useEffect, useCallback } from 'react';
import { ErrorBoundary } from "@appsignal/react";
import { ETiers } from '@appComponents/components/IdentityStatusLabel/IIdentityStatusLabel';
import { Notification } from './components/Notification/Notification';
import { LightboxComponent } from '@company/components/LightboxComponent/LightboxComponent';
import {
  AgreementTemplate,
  BrokerAgreement,
} from './components/AgreementTemplate/AgreementTemplate';
import Loader from '@appComponents/Loader/Loader';
import AgreementsService from '@services/Agreements.service';
import { AgreementRow } from './components/AgreementRow/AgreementRow';

import { appsignal } from "./../../appsignal";
import * as style from './InvestmentAgreementAppStyle.module.scss'

export enum EAgreementType {
  licence = 'licence',
  broker = 'broker',
}

type LicenceAgreement = {
  created_at: string;
  id: string;
  investor_id: number;
  licence_id: number;
  pitch_id: number;
  status: 'unsigned' | 'signed';
  updated_at: string;
};

type UserAgreement = {
  id: number;
  user_id: number;
  pitch_id: number;
  agreement_id: string;
  created_at: Date;
  signed: boolean;
  reference: string;
};

type Props = {
  licence: BrokerAgreement;
  brokerAgreements: BrokerAgreement[];
  brokerAgreementsToSign: UserAgreement[];
  isUsInvestor: boolean;
  tier: ETiers;
  pitchId: number;
  licenceSignedByUser: LicenceAgreement;
};

export const InvestmentAgreementApp = ({
  brokerAgreements,
  brokerAgreementsToSign,
  pitchId,
  licence,
  licenceSignedByUser,
}: Props) => {
  const [showAgreement, setShowAgreement] = useState(false);
  const [currentUserAgreements, setCurrentUserAgreements] = useState(
    brokerAgreementsToSign
  );
  const [agreementToSign, setAgreementToSign] = useState<BrokerAgreement>();

  const [
    currentLicenceUserAgreement,
    setCurrentLicenceUserAgreement,
  ] = useState(licenceSignedByUser);

  useEffect(() => {
    if (
      currentUserAgreements.every((agr) => agr.signed) &&
      currentLicenceUserAgreement.status === 'signed'
    ) {
      const investmentForm = document.getElementById('new_investment');
      investmentForm?.classList.remove('investment-form-disabled');

      window.scroll({
        top: investmentForm?.getBoundingClientRect().top,
        behavior: 'smooth',
      });
    }
  }, [currentLicenceUserAgreement, currentUserAgreements]);

  if (!licenceSignedByUser) {
    throw new Error('Pitch investment - no licence agreement assigned');
  }
  if (!brokerAgreements) {
    throw new Error('Pitch investment - no broker agreement assigned');
  }

  const brokerAgreementsToRender = ((): BrokerAgreement[] =>
    brokerAgreementsToSign.map(
      (agreement) =>
        brokerAgreements.find((broker) => broker.id === agreement.agreement_id)!
    ))();

  const notification = (
    <Notification>
      <>
        <span>
          Please review and sign the broker terms applicable to this investment.
        </span>
        <br />
        <span>
          If you are a US person, you will also need to accept the terms of the
          US broker
        </span>
      </>
    </Notification>
  );

  const showSignAgreement = async(agreementType: EAgreementType, id: string) => {

      const request =
        agreementType === EAgreementType.licence
          ? AgreementsService.getLicenceAgreement
          : AgreementsService.getBrokerAgreement;

      const agreementId =
        agreementType === EAgreementType.licence ? licenceSignedByUser.id : id;

      const response = await request(agreementId);

      setAgreementToSign({ ...response.data, type: agreementType });
      setShowAgreement(true);

    };

  const onBrokerAgreementSubmit = useCallback(
    async (id: string) => {
      const current = currentUserAgreements?.find(
        (agreement) => agreement.agreement_id === id
      );
      if (!current) return;

      try {
        await AgreementsService.signBrokerAgreement(id, pitchId);

        setCurrentUserAgreements((prev: UserAgreement[]) => {
          const updatedAgreement = { ...current, signed: true };
          const agreements = [
            ...prev.filter((agr) => agr.agreement_id !== id),
            updatedAgreement,
          ];

          return agreements;
        });
      } catch (error) {
        throw new Error('AgreementsService.signBrokerAgreement failed' + error);
      }
    },
    [currentUserAgreements, setCurrentUserAgreements]
  );

  const onLicenceAgreementSubmit = async (id: string) => {
    try {
      await AgreementsService.signLicenceAgreement(id);
      setCurrentLicenceUserAgreement((prev) => ({ ...prev, status: 'signed' }));
    } catch (error) {
      throw new Error('AgreementsService.signLicenceAgreement failed' + error);
    }
  };

  const closeSignAgreement = () => setShowAgreement(false);

  const isBrokerAgreementSigned = (agreementId: string) =>
    currentUserAgreements?.find(
      (agreement) => agreement.agreement_id === agreementId
    )?.signed || false;

  const isLicenceAgreementSigned =
    currentLicenceUserAgreement.status === 'signed';

  const isAgreementBlocked = (index: number) =>
    (index !== 0 &&
      currentUserAgreements &&
      !currentUserAgreements.some((agr) => agr.signed)) ||
    currentLicenceUserAgreement.status !== 'signed';

  return (
    <ErrorBoundary instance={appsignal}>
      <div className={style.brokerAgreementApp}>
        {notification}

        <AgreementRow
          label="Brokerage agreement required"
          licenceId={licence.id}
          isBlocked={false}
          isSigned={isLicenceAgreementSigned}
          type={EAgreementType.licence}
          handleShowAgreementClick={() =>
            showSignAgreement(EAgreementType.licence, licence.id)
          }
        />

        {brokerAgreementsToRender.map((brokerAgreement, index) => (
          <AgreementRow
            key={brokerAgreement.id}
            label="Brokerage agreement required"
            licenceId={brokerAgreement.id}
            isBlocked={isAgreementBlocked(index)}
            isSigned={isBrokerAgreementSigned(brokerAgreement.id)}
            type={EAgreementType.broker}
            handleShowAgreementClick={() =>
              showSignAgreement(EAgreementType.broker, brokerAgreement.id)
            }
          />
        ))}

        <LightboxComponent
          isVisible={showAgreement}
          closePreview={closeSignAgreement}
        >
          {agreementToSign ? (
            <AgreementTemplate
              agreement={agreementToSign}
              closeModal={closeSignAgreement}
              onSubmit={
                agreementToSign.type === EAgreementType.broker
                  ? onBrokerAgreementSubmit
                  : onLicenceAgreementSubmit
              }
            />
          ) : (
            <Loader />
          )}
        </LightboxComponent>
      </div>
    </ErrorBoundary>
  );
};
export default (props: Props) => <React.StrictMode><InvestmentAgreementApp {...props} /></React.StrictMode>;
